/**
 * STARDUST — vertical meteor shooter with collectible-ammo twist (MVP).
 *
 * Heritage: Astrosmash (descending meteors + saucer interlopers), Asteroids
 * (rocks that split). Original mechanic: white shards. Every cracked meteor
 * drops a shard; touching one refills 1 ammo. Run out of ammo and you can
 * only dodge — so blasting every shard out of the sky starves you of bullets.
 *
 * Architecture:
 *   - Fixed 480×270 vertical playfield, starfield scrolling down for depth
 *   - Player ship lives in a wide horizontal band near the bottom; can
 *     thrust up/down within that band but never leaves it
 *   - Meteors descend from off-screen top; split LARGE→MED→SMALL on hit
 *   - Saucers cross horizontally on a long timer and drop a single bomb
 *   - Shards spawn from cracked meteors and drift down past the player
 *
 * Determinism: all randomness routes through ctx.random. Same (seed,
 * inputFrames) → same final score; verified by the 1000-iter test.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  CRASH_FLASH,
  FLASH_HIT,
  HUD_AMMO,
  HUD_AMMO_LOW,
  HUD_SCORE,
  HUD_TEXT,
  HUD_WAVE,
  METEOR_LARGE,
  METEOR_LARGE_DARK,
  METEOR_MED,
  METEOR_MED_DARK,
  METEOR_SMALL,
  METEOR_SMALL_DARK,
  SAUCER_BODY,
  SAUCER_BODY_DARK,
  SAUCER_BOMB,
  SAUCER_COCKPIT,
  SHARD_BODY,
  SHARD_GLINT,
  SHIP_BODY,
  SHIP_BODY_HI,
  SHIP_BULLET,
  SHIP_THRUSTER,
  SHIP_THRUSTER_HOT,
  SPACE_BG,
  SPACE_BG_2,
  STAR_BRIGHT,
  STAR_DIM,
  STAR_MID,
} from './palette.js';

const WIDTH = 480;
const HEIGHT = 270;

// Player ship.
const SHIP_W = 22;
const SHIP_H = 16;
const SHIP_BAND_TOP = HEIGHT - 110; // player can thrust up to here
const SHIP_BAND_BOTTOM = HEIGHT - 28; // and down to here
const SHIP_ACCEL = 0.45;
const SHIP_DAMP = 0.9;
const SHIP_MAX_VEL = 3.6;

// Bullets.
const BULLET_W = 3;
const BULLET_H = 8;
const BULLET_VEL = 7;
const BULLET_COOLDOWN = 8; // ticks between shots
const STARTING_AMMO = 30;
const MAX_AMMO = 60;

// Meteor sizes — tier 0 = LARGE, tier 1 = MED, tier 2 = SMALL.
const METEOR_DIM = [28, 18, 10] as const;
const METEOR_SCORE = [25, 50, 100] as const;
const METEOR_BASE_VY = 0.9;
const METEOR_VY_VAR = 0.7;
const METEOR_VX_VAR = 1.2; // small lateral drift

// How many shards a meteor drops when broken (tier-indexed).
const METEOR_SHARDS = [2, 1, 1] as const;

// Saucer.
const SAUCER_W = 32;
const SAUCER_H = 14;
const SAUCER_VX = 2.2;
const SAUCER_SPAWN_INTERVAL = 540; // ~9s
const SAUCER_BOMB_W = 4;
const SAUCER_BOMB_H = 10;
const SAUCER_BOMB_VY = 3.5;
const SAUCER_SCORE = 250;

// Shard pickup.
const SHARD_W = 6;
const SHARD_H = 6;
const SHARD_VY = 1.4;
const SHARD_AMMO_VALUE = 3;

// Wave progression. Every WAVE_DURATION ticks the wave advances, raising
// spawn pressure. Cap at MAX_WAVES so things stay survivable.
const WAVE_DURATION = 1800; // 30s per wave at 60Hz
const MAX_WAVES = 8;
const METEOR_SPAWN_BASE = 75; // ticks between meteor spawns at wave 1
const WAVE_BANNER_TICKS = 90;

// Crash + game-over.
const CRASH_FLASH_TICKS = 18;
const SHAKE_TICKS = 14;
const SHAKE_MAGNITUDE = 3;

type Phase = 'playing' | 'crashed' | 'game-over';

interface Vec {
  x: number;
  y: number;
}

interface Meteor extends Vec {
  vx: number;
  vy: number;
  tier: 0 | 1 | 2;
}

interface Bullet extends Vec {
  vy: number;
}

interface Shard extends Vec {
  vy: number;
}

interface Saucer extends Vec {
  vx: number;
  bombDropped: boolean;
}

interface Bomb extends Vec {
  vy: number;
}

interface Star extends Vec {
  vy: number;
  brightness: 0 | 1 | 2; // 0=dim, 1=mid, 2=bright
}

export class StardustInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Player.
  private shipX = WIDTH / 2 - SHIP_W / 2;
  private shipY = HEIGHT - 50;
  private shipVX = 0;
  private shipVY = 0;
  private inLeft = false;
  private inRight = false;
  private inUp = false;
  private inDown = false;
  private inFire = false;
  private fireCooldown = 0;

  // Entities.
  private bullets: Bullet[] = [];
  private meteors: Meteor[] = [];
  private shards: Shard[] = [];
  private saucers: Saucer[] = [];
  private bombs: Bomb[] = [];
  private stars: Star[] = [];

  // Spawn timers.
  private meteorSpawnTimer = 60; // first meteor ~1s in
  private saucerSpawnTimer = SAUCER_SPAWN_INTERVAL;

  // Score + ammo.
  private score = 0;
  private ammo = STARTING_AMMO;

  // Wave.
  private wave = 1;
  private waveTickCounter = 0;
  private waveBannerTicks = WAVE_BANNER_TICKS;

  // Lifecycle.
  private phase: Phase = 'playing';
  private crashFlashLeft = 0;
  private shakeTicksLeft = 0;
  private finalScore = 0;
  private finalWave = 1;
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.seedStarfield();
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inLeft = state.left;
    this.inRight = state.right;
    this.inUp = state.up;
    this.inDown = state.down;
    // Space is bound to `start` at the cabinet level (arcade convention),
    // not `fireA`. Treat both as fire so the pause-overlay-listed Z and
    // the player's natural reflex Space both work.
    this.inFire = state.fireA || state.fireB || state.start;
  }

  update(_dt: number): void {
    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.crashFlashLeft -= 1;
      if (this.shakeTicksLeft > 0) this.shakeTicksLeft -= 1;
      // Keep stars + meteors drifting cosmetically during crash flash.
      this.advanceStarfield();
      if (this.crashFlashLeft <= 0) {
        this.phase = 'game-over';
        this.finalScore = Math.floor(this.score);
        this.finalWave = this.wave;
        this.ctx.events.emit('death', { remainingLives: 0 });
      }
      return;
    }

    if (this.shakeTicksLeft > 0) this.shakeTicksLeft -= 1;

    this.advanceStarfield();
    this.updateShip();
    this.updateBullets();
    this.updateMeteors();
    this.updateShards();
    this.updateSaucers();
    this.updateBombs();
    this.updateWave();
    this.handleCollisions();
  }

  render(g: CanvasRenderingContext2D): void {
    // Background — two-tone gradient stripe for depth.
    g.fillStyle = SPACE_BG;
    g.fillRect(0, 0, WIDTH, HEIGHT);
    g.fillStyle = SPACE_BG_2;
    g.fillRect(0, 0, WIDTH, HEIGHT / 2);

    // Screen-shake.
    if (this.shakeTicksLeft > 0) {
      const t = this.shakeTicksLeft;
      const intensity = (t / SHAKE_TICKS) * SHAKE_MAGNITUDE;
      const sx = Math.sin(t * 12.91) * intensity;
      const sy = Math.cos(t * 9.37) * intensity;
      g.save();
      g.translate(Math.round(sx), Math.round(sy));
    }

    // Starfield.
    for (const s of this.stars) {
      const c = s.brightness === 2 ? STAR_BRIGHT : s.brightness === 1 ? STAR_MID : STAR_DIM;
      g.fillStyle = c;
      g.fillRect(Math.floor(s.x), Math.floor(s.y), 1, 1);
    }

    // Shards behind meteors so meteors visually overlap them.
    for (const s of this.shards) this.drawShard(g, s);

    // Meteors.
    for (const m of this.meteors) this.drawMeteor(g, m);

    // Saucers + bombs.
    for (const sa of this.saucers) this.drawSaucer(g, sa);
    for (const b of this.bombs) this.drawBomb(g, b);

    // Bullets.
    for (const b of this.bullets) {
      g.fillStyle = SHIP_BULLET;
      g.fillRect(b.x, b.y, BULLET_W, BULLET_H);
    }

    // Ship.
    if (this.phase !== 'crashed' || this.crashFlashLeft % 4 < 2) {
      this.drawShip(g);
    }

    // Crash flash.
    if (this.phase === 'crashed' && this.crashFlashLeft > 0) {
      const a = this.crashFlashLeft / CRASH_FLASH_TICKS;
      g.globalAlpha = a * 0.6;
      g.fillStyle = CRASH_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    if (this.shakeTicksLeft > 0) g.restore();

    this.renderHUD(g);

    if (this.phase === 'game-over') this.renderGameOver(g);
  }

  resize(_w: number, _h: number): void {
    /* 480x270 internal — cabinet scales */
  }

  pause(): void {
    /* no audio yet */
  }

  resume(): void {
    /* no audio yet */
  }

  teardown(): void {
    this.bullets = [];
    this.meteors = [];
    this.shards = [];
    this.saucers = [];
    this.bombs = [];
    this.stars = [];
  }

  getScore(): number {
    return Math.floor(this.score);
  }

  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }

  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // --------------------------------------------------------------------------
  // Update internals
  // --------------------------------------------------------------------------

  private updateShip(): void {
    // Horizontal.
    if (this.inLeft && !this.inRight) this.shipVX -= SHIP_ACCEL;
    else if (this.inRight && !this.inLeft) this.shipVX += SHIP_ACCEL;
    else {
      this.shipVX *= SHIP_DAMP;
      if (Math.abs(this.shipVX) < 0.05) this.shipVX = 0;
    }
    if (this.shipVX > SHIP_MAX_VEL) this.shipVX = SHIP_MAX_VEL;
    if (this.shipVX < -SHIP_MAX_VEL) this.shipVX = -SHIP_MAX_VEL;
    this.shipX += this.shipVX;
    if (this.shipX < 4) {
      this.shipX = 4;
      this.shipVX = 0;
    }
    if (this.shipX > WIDTH - SHIP_W - 4) {
      this.shipX = WIDTH - SHIP_W - 4;
      this.shipVX = 0;
    }

    // Vertical (constrained band).
    if (this.inUp && !this.inDown) this.shipVY -= SHIP_ACCEL * 0.7;
    else if (this.inDown && !this.inUp) this.shipVY += SHIP_ACCEL * 0.7;
    else {
      this.shipVY *= SHIP_DAMP;
      if (Math.abs(this.shipVY) < 0.05) this.shipVY = 0;
    }
    if (this.shipVY > SHIP_MAX_VEL * 0.6) this.shipVY = SHIP_MAX_VEL * 0.6;
    if (this.shipVY < -SHIP_MAX_VEL * 0.6) this.shipVY = -SHIP_MAX_VEL * 0.6;
    this.shipY += this.shipVY;
    if (this.shipY < SHIP_BAND_TOP) {
      this.shipY = SHIP_BAND_TOP;
      this.shipVY = 0;
    }
    if (this.shipY > SHIP_BAND_BOTTOM) {
      this.shipY = SHIP_BAND_BOTTOM;
      this.shipVY = 0;
    }

    if (this.fireCooldown > 0) this.fireCooldown -= 1;
    if (this.inFire && this.fireCooldown <= 0) {
      if (this.ammo > 0) {
        this.bullets.push({
          x: this.shipX + SHIP_W / 2 - BULLET_W / 2,
          y: this.shipY - BULLET_H,
          vy: -BULLET_VEL,
        });
        this.fireCooldown = BULLET_COOLDOWN;
        this.ammo -= 1;
        this.ctx.audio.sfx('shoot');
      } else {
        // Click on empty so the player knows why nothing happened.
        this.fireCooldown = BULLET_COOLDOWN * 2;
        this.ctx.audio.sfx('empty');
      }
    }
  }

  private updateBullets(): void {
    for (const b of this.bullets) b.y += b.vy;
    this.bullets = this.bullets.filter((b) => b.y > -BULLET_H);
  }

  private updateMeteors(): void {
    for (const m of this.meteors) {
      m.x += m.vx;
      m.y += m.vy;
    }
    // Drop off-screen bottom/sides.
    this.meteors = this.meteors.filter(
      (m) => m.y < HEIGHT + 40 && m.x > -60 && m.x < WIDTH + 60
    );

    // Spawn.
    this.meteorSpawnTimer -= 1;
    if (this.meteorSpawnTimer <= 0) {
      this.spawnMeteor();
      const waveSpeedup = 1 + (this.wave - 1) * 0.25;
      this.meteorSpawnTimer = Math.floor(
        (METEOR_SPAWN_BASE + this.ctx.random.int(0, 30)) / waveSpeedup
      );
    }
  }

  private updateShards(): void {
    for (const s of this.shards) s.y += s.vy;
    this.shards = this.shards.filter((s) => s.y < HEIGHT + 20);
  }

  private updateSaucers(): void {
    for (const sa of this.saucers) {
      sa.x += sa.vx;
      // Drop bomb once when roughly aligned with player.
      if (!sa.bombDropped && Math.abs(sa.x + SAUCER_W / 2 - (this.shipX + SHIP_W / 2)) < 40) {
        this.bombs.push({
          x: sa.x + SAUCER_W / 2 - SAUCER_BOMB_W / 2,
          y: sa.y + SAUCER_H,
          vy: SAUCER_BOMB_VY,
        });
        sa.bombDropped = true;
      }
    }
    this.saucers = this.saucers.filter((sa) => sa.x > -SAUCER_W - 20 && sa.x < WIDTH + 20);

    this.saucerSpawnTimer -= 1;
    if (this.saucerSpawnTimer <= 0) {
      this.spawnSaucer();
      this.ctx.audio.sfx('saucer');
      const waveSpeedup = 1 + (this.wave - 1) * 0.15;
      this.saucerSpawnTimer = Math.floor(SAUCER_SPAWN_INTERVAL / waveSpeedup);
    }
  }

  private updateBombs(): void {
    for (const b of this.bombs) b.y += b.vy;
    this.bombs = this.bombs.filter((b) => b.y < HEIGHT + 10);
  }

  private updateWave(): void {
    this.waveTickCounter += 1;
    if (this.waveTickCounter >= WAVE_DURATION && this.wave < MAX_WAVES) {
      this.wave += 1;
      this.waveTickCounter = 0;
      this.waveBannerTicks = WAVE_BANNER_TICKS;
      this.ctx.audio.sfx('wave-up');
      this.ctx.events.emit('levelComplete', { level: this.wave - 1, bonus: 0 });
    }
    if (this.waveBannerTicks > 0) this.waveBannerTicks -= 1;
  }

  private handleCollisions(): void {
    // Bullets ↔ meteors. Walk both arrays once, mark indices to remove.
    const meteorsToBreak: number[] = [];
    const bulletsToRemove: Set<number> = new Set();
    for (let bi = 0; bi < this.bullets.length; bi++) {
      const b = this.bullets[bi]!;
      for (let mi = 0; mi < this.meteors.length; mi++) {
        if (meteorsToBreak.includes(mi)) continue;
        const m = this.meteors[mi]!;
        const dim = METEOR_DIM[m.tier];
        if (
          b.x < m.x + dim &&
          b.x + BULLET_W > m.x &&
          b.y < m.y + dim &&
          b.y + BULLET_H > m.y
        ) {
          meteorsToBreak.push(mi);
          bulletsToRemove.add(bi);
          break;
        }
      }
    }
    // Apply breaks. Iterate descending so splice indices stay valid.
    meteorsToBreak.sort((a, b) => b - a);
    for (const mi of meteorsToBreak) {
      const m = this.meteors[mi]!;
      this.breakMeteor(m);
      this.meteors.splice(mi, 1);
    }
    if (bulletsToRemove.size > 0) {
      this.bullets = this.bullets.filter((_, i) => !bulletsToRemove.has(i));
    }

    // Bullets ↔ saucers.
    const saucersToKill: number[] = [];
    const bulletsToRemove2: Set<number> = new Set();
    for (let bi = 0; bi < this.bullets.length; bi++) {
      const b = this.bullets[bi]!;
      for (let si = 0; si < this.saucers.length; si++) {
        if (saucersToKill.includes(si)) continue;
        const sa = this.saucers[si]!;
        if (
          b.x < sa.x + SAUCER_W &&
          b.x + BULLET_W > sa.x &&
          b.y < sa.y + SAUCER_H &&
          b.y + BULLET_H > sa.y
        ) {
          saucersToKill.push(si);
          bulletsToRemove2.add(bi);
          this.score += SAUCER_SCORE;
          this.ctx.events.emit('score', {
            score: Math.floor(this.score),
            delta: SAUCER_SCORE,
          });
          break;
        }
      }
    }
    saucersToKill.sort((a, b) => b - a);
    for (const si of saucersToKill) this.saucers.splice(si, 1);
    if (bulletsToRemove2.size > 0) {
      this.bullets = this.bullets.filter((_, i) => !bulletsToRemove2.has(i));
    }

    // Ship ↔ shards (pickup).
    const sx0 = this.shipX;
    const sx1 = this.shipX + SHIP_W;
    const sy0 = this.shipY;
    const sy1 = this.shipY + SHIP_H;
    this.shards = this.shards.filter((sh) => {
      const hit =
        sh.x < sx1 && sh.x + SHARD_W > sx0 && sh.y < sy1 && sh.y + SHARD_H > sy0;
      if (hit) {
        this.ammo = Math.min(MAX_AMMO, this.ammo + SHARD_AMMO_VALUE);
        this.ctx.audio.sfx('pickup');
      }
      return !hit;
    });

    // Ship ↔ meteors, bombs, saucers — death checks.
    for (const m of this.meteors) {
      const dim = METEOR_DIM[m.tier];
      if (m.x < sx1 && m.x + dim > sx0 && m.y < sy1 && m.y + dim > sy0) {
        this.die();
        return;
      }
    }
    for (const b of this.bombs) {
      if (
        b.x < sx1 &&
        b.x + SAUCER_BOMB_W > sx0 &&
        b.y < sy1 &&
        b.y + SAUCER_BOMB_H > sy0
      ) {
        this.die();
        return;
      }
    }
    for (const sa of this.saucers) {
      if (sa.x < sx1 && sa.x + SAUCER_W > sx0 && sa.y < sy1 && sa.y + SAUCER_H > sy0) {
        this.die();
        return;
      }
    }
  }

  private breakMeteor(m: Meteor): void {
    const score = METEOR_SCORE[m.tier];
    this.score += score;
    this.ctx.audio.sfx('crack');
    this.ctx.events.emit('score', { score: Math.floor(this.score), delta: score });

    // Spawn shards.
    const shardCount = METEOR_SHARDS[m.tier];
    const dim = METEOR_DIM[m.tier];
    for (let i = 0; i < shardCount; i++) {
      this.shards.push({
        x: m.x + dim / 2 - SHARD_W / 2 + (this.ctx.random.next() - 0.5) * dim,
        y: m.y + dim / 2,
        vy: SHARD_VY + this.ctx.random.next() * 0.6,
      });
    }

    // Split into two smaller meteors if not already smallest tier.
    if (m.tier < 2) {
      const newTier = (m.tier + 1) as 0 | 1 | 2;
      const newDim = METEOR_DIM[newTier];
      const speedup = 1.15; // shards travel slightly faster
      const baseVy = m.vy * speedup;
      for (let i = 0; i < 2; i++) {
        const sign = i === 0 ? -1 : 1;
        this.meteors.push({
          x: m.x + dim / 2 - newDim / 2,
          y: m.y + dim / 2 - newDim / 2,
          vx: m.vx + sign * (0.8 + this.ctx.random.next() * 0.6),
          vy: baseVy,
          tier: newTier,
        });
      }
    }
  }

  private die(): void {
    this.phase = 'crashed';
    this.crashFlashLeft = CRASH_FLASH_TICKS;
    this.shakeTicksLeft = SHAKE_TICKS;
    this.ctx.audio.sfx('crash');
  }

  private spawnMeteor(): void {
    // Always spawn LARGE tier; lateral position random, drift slight.
    const x = this.ctx.random.int(20, WIDTH - 20 - METEOR_DIM[0]);
    const vx = (this.ctx.random.next() - 0.5) * METEOR_VX_VAR;
    const waveSpeed = 1 + (this.wave - 1) * 0.12;
    const vy =
      (METEOR_BASE_VY + this.ctx.random.next() * METEOR_VY_VAR) * waveSpeed;
    this.meteors.push({
      x,
      y: -METEOR_DIM[0],
      vx,
      vy,
      tier: 0,
    });
  }

  private spawnSaucer(): void {
    const fromLeft = this.ctx.random.next() < 0.5;
    const y = 30 + this.ctx.random.next() * 60;
    this.saucers.push({
      x: fromLeft ? -SAUCER_W : WIDTH + SAUCER_W,
      y,
      vx: fromLeft ? SAUCER_VX : -SAUCER_VX,
      bombDropped: false,
    });
  }

  private seedStarfield(): void {
    // 80 stars at varied brightness + speed for parallax.
    for (let i = 0; i < 80; i++) {
      const brightness = this.ctx.random.int(0, 2) as 0 | 1 | 2;
      this.stars.push({
        x: this.ctx.random.next() * WIDTH,
        y: this.ctx.random.next() * HEIGHT,
        vy: 0.3 + brightness * 0.4,
        brightness,
      });
    }
  }

  private advanceStarfield(): void {
    for (const s of this.stars) {
      s.y += s.vy;
      if (s.y > HEIGHT) {
        s.y = 0;
        s.x = this.ctx.random.next() * WIDTH;
      }
    }
  }

  // --------------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------------

  private drawShip(g: CanvasRenderingContext2D): void {
    const x = this.shipX;
    const y = this.shipY;
    // Body shadow.
    g.fillStyle = '#000';
    g.fillRect(x + 1, y + 1, SHIP_W, SHIP_H);
    // Hull.
    g.fillStyle = SHIP_BODY;
    g.fillRect(x + 2, y + 4, SHIP_W - 4, SHIP_H - 4);
    // Nose triangle (3-wide pixel ramp).
    g.fillRect(x + SHIP_W / 2 - 5, y + 2, 10, 2);
    g.fillRect(x + SHIP_W / 2 - 3, y, 6, 2);
    g.fillStyle = SHIP_BODY_HI;
    g.fillRect(x + SHIP_W / 2 - 2, y, 4, 4);
    // Wings.
    g.fillStyle = SHIP_BODY;
    g.fillRect(x, y + SHIP_H - 8, 4, 4);
    g.fillRect(x + SHIP_W - 4, y + SHIP_H - 8, 4, 4);
    // Thruster (animated when thrusting up).
    const thrusting = this.inUp;
    g.fillStyle = thrusting ? SHIP_THRUSTER_HOT : SHIP_THRUSTER;
    g.fillRect(x + SHIP_W / 2 - 3, y + SHIP_H, 6, thrusting ? 5 : 3);
    g.fillStyle = SHIP_THRUSTER;
    g.fillRect(x + SHIP_W / 2 - 1, y + SHIP_H + (thrusting ? 5 : 3), 2, thrusting ? 3 : 2);
  }

  private drawMeteor(g: CanvasRenderingContext2D, m: Meteor): void {
    const dim = METEOR_DIM[m.tier];
    const color = m.tier === 0 ? METEOR_LARGE : m.tier === 1 ? METEOR_MED : METEOR_SMALL;
    const dark = m.tier === 0 ? METEOR_LARGE_DARK : m.tier === 1 ? METEOR_MED_DARK : METEOR_SMALL_DARK;
    // Shadow.
    g.fillStyle = '#000';
    g.fillRect(m.x + 1, m.y + 1, dim, dim);
    // Body.
    g.fillStyle = color;
    g.fillRect(m.x, m.y, dim, dim);
    // Crater detail (just dark spots so it doesn't look like a flat square).
    g.fillStyle = dark;
    if (m.tier === 0) {
      g.fillRect(m.x + 4, m.y + 4, 6, 6);
      g.fillRect(m.x + dim - 10, m.y + dim - 10, 6, 6);
      g.fillRect(m.x + dim - 8, m.y + 4, 4, 4);
    } else if (m.tier === 1) {
      g.fillRect(m.x + 3, m.y + 3, 4, 4);
      g.fillRect(m.x + dim - 7, m.y + dim - 7, 4, 4);
    } else {
      g.fillRect(m.x + 2, m.y + 2, 3, 3);
    }
  }

  private drawShard(g: CanvasRenderingContext2D, s: Shard): void {
    g.fillStyle = SHARD_BODY;
    g.fillRect(s.x, s.y, SHARD_W, SHARD_H);
    g.fillStyle = SHARD_GLINT;
    g.fillRect(s.x + 1, s.y + 1, 2, 2);
    g.fillRect(s.x + SHARD_W - 2, s.y + SHARD_H - 2, 1, 1);
  }

  private drawSaucer(g: CanvasRenderingContext2D, sa: Saucer): void {
    g.fillStyle = '#000';
    g.fillRect(sa.x + 1, sa.y + 1, SAUCER_W, SAUCER_H);
    // Body — wider middle band.
    g.fillStyle = SAUCER_BODY_DARK;
    g.fillRect(sa.x, sa.y + SAUCER_H - 4, SAUCER_W, 4);
    g.fillStyle = SAUCER_BODY;
    g.fillRect(sa.x + 4, sa.y + 4, SAUCER_W - 8, SAUCER_H - 8);
    g.fillRect(sa.x + 2, sa.y + 6, SAUCER_W - 4, 4);
    // Cockpit dome.
    g.fillStyle = SAUCER_COCKPIT;
    g.fillRect(sa.x + SAUCER_W / 2 - 4, sa.y, 8, 4);
    g.fillRect(sa.x + SAUCER_W / 2 - 2, sa.y - 1, 4, 1);
  }

  private drawBomb(g: CanvasRenderingContext2D, b: Bomb): void {
    g.fillStyle = SAUCER_BOMB;
    g.fillRect(b.x, b.y, SAUCER_BOMB_W, SAUCER_BOMB_H);
    g.fillStyle = FLASH_HIT;
    g.fillRect(b.x + 1, b.y + 1, 1, 2);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('SCORE', 8, 6);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 18);

    g.textAlign = 'center';
    g.fillStyle = HUD_TEXT;
    g.fillText(`WAVE ${this.wave}`, WIDTH / 2, 6);
    g.fillStyle = HUD_WAVE;
    const waveProgress = Math.min(1, this.waveTickCounter / WAVE_DURATION);
    const barW = 60;
    g.fillRect(WIDTH / 2 - barW / 2, 22, Math.floor(barW * waveProgress), 2);
    g.strokeStyle = HUD_WAVE;
    g.strokeRect(WIDTH / 2 - barW / 2 - 0.5, 21.5, barW + 1, 3);

    g.textAlign = 'right';
    g.fillStyle = HUD_TEXT;
    g.fillText('AMMO', WIDTH - 8, 6);
    g.fillStyle = this.ammo < 8 ? HUD_AMMO_LOW : HUD_AMMO;
    g.fillText(String(this.ammo).padStart(3, '0'), WIDTH - 8, 18);

    g.textAlign = 'left';

    if (this.waveBannerTicks > 0 && this.wave > 1) {
      const t = this.waveBannerTicks / WAVE_BANNER_TICKS;
      const a = t > 0.66 ? (1 - t) * 3 : t / 0.66;
      g.globalAlpha = a;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = 'bold 28px ui-monospace, monospace';
      g.fillStyle = HUD_WAVE;
      g.fillText(`WAVE ${this.wave}`, WIDTH / 2, HEIGHT / 2 - 10);
      g.font = '10px ui-monospace, monospace';
      g.fillStyle = HUD_TEXT;
      g.fillText('METEORS INCOMING', WIDTH / 2, HEIGHT / 2 + 14);
      g.globalAlpha = 1;
      g.textAlign = 'left';
      g.textBaseline = 'top';
    }
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0,0,0,0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = '24px ui-monospace, monospace';
    g.fillStyle = CRASH_FLASH;
    g.fillText('GAME OVER', WIDTH / 2, HEIGHT / 2 - 30);

    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`SCORE ${String(this.finalScore).padStart(6, '0')}`, WIDTH / 2, HEIGHT / 2);
    g.fillStyle = HUD_WAVE;
    g.fillText(`WAVE ${this.finalWave}`, WIDTH / 2, HEIGHT / 2 + 16);

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_TEXT;
    g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 50);
  }
}
